import type { AllMembershipPlanType, SwitchableMembershipPlanType } from '@frontend/api-client';
import type { Card, PromotionDetails } from '@frontend/api-types';
import type { Reducer } from 'react';

export const PlanManagementStep = {
  cancel: 'cancel',
  failedPayment: 'failedPayment',
  processing: 'processing',
  selectCard: 'selectCard',
  selectSubscription: 'selectSubscription',
  success: 'success',
} as const;

export type PlanManagementStep = (typeof PlanManagementStep)[keyof typeof PlanManagementStep];

export type CurrentSubscriptionPlan = {
  planType: AllMembershipPlanType;
};

export type SubscriptionPlan = {
  planType: SwitchableMembershipPlanType;
  promotion?: {
    promoCode: string;
    promotionDescription: string;
    promotionDetails: PromotionDetails;
  };
};

export type PlanManagementState = {
  canGoBack?: boolean;
  card?: Card;
  currentSubscriptionPlan?: CurrentSubscriptionPlan;
  selectedSubscriptionPlan?: SubscriptionPlan;
  step: PlanManagementStep;
};

export type PlanManagementStateMachineAction =
  | {
      card: Card;
      currentSubscriptionPlan?: CurrentSubscriptionPlan;
      selectedSubscriptionPlan: SubscriptionPlan;
      type: 'pay';
    }
  | {
      card?: Card;
      currentSubscriptionPlan?: CurrentSubscriptionPlan;
      selectedSubscriptionPlan?: SubscriptionPlan;
      type: 'completePayment';
    }
  | {
      card?: Card;
      currentSubscriptionPlan?: CurrentSubscriptionPlan;
      selectedSubscriptionPlan?: SubscriptionPlan;
      type: 'failPayment';
    }
  | {
      currentSubscriptionPlan: CurrentSubscriptionPlan;
      type: 'continueWithCancelSubscription';
    }
  | {
      currentSubscriptionPlan?: CurrentSubscriptionPlan;
      selectedSubscriptionPlan: SubscriptionPlan;
      type: 'continueWithSelectSubscription';
    }
  | {
      currentSubscriptionPlan?: CurrentSubscriptionPlan;
      type: 'selectSubscription';
    }
  | { currentSubscriptionPlan: CurrentSubscriptionPlan; type: 'cancelSubscription' }
  | { currentSubscriptionPlan?: CurrentSubscriptionPlan; selectedSubscriptionPlan?: SubscriptionPlan; type: 'back' }
  | { currentSubscriptionPlan?: CurrentSubscriptionPlan; type: 'reset' };

type ActionType = PlanManagementStateMachineAction['type'];

type ExtractAction<T extends ActionType> = Extract<PlanManagementStateMachineAction, { type: T }>;

type Handler<T extends ActionType = ActionType> = (
  prev: PlanManagementState,
  action: ExtractAction<T>,
) => PlanManagementState;

type HandlersLookup = Partial<{
  [Step in PlanManagementStep]: Partial<{
    [Action in ActionType]: Handler<Action>;
  }>;
}>;

const canGoBack = (step: PlanManagementStep, goBackSteps: PlanManagementStep[] = ['selectCard', 'cancel']) => {
  return goBackSteps.includes(step);
};

const DEFAULT_HANDLERS: HandlersLookup = {
  cancel: {
    back: (_prev, action) => ({
      canGoBack: canGoBack('selectSubscription'),
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.selectSubscription,
    }),

    continueWithCancelSubscription: (_prev, action) => ({
      canGoBack: false,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      step: PlanManagementStep.processing,
    }),
  },

  failedPayment: {
    back: (_prev, action) => ({
      canGoBack: false,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.selectSubscription,
    }),
  },

  processing: {
    completePayment: (_prev, action) => ({
      canGoBack: false,
      card: action.card,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.success,
    }),

    failPayment: (_prev, action) => ({
      canGoBack: false,
      card: action.card,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.failedPayment,
    }),
  },

  selectCard: {
    back: (prev, action) => {
      if (!canGoBack(prev.step)) return prev;

      return {
        canGoBack: canGoBack('selectSubscription'),
        currentSubscriptionPlan: action.currentSubscriptionPlan,
        selectedSubscriptionPlan: action.selectedSubscriptionPlan,
        step: PlanManagementStep.selectSubscription,
      };
    },

    pay: (_prev, action) => ({
      canGoBack: false,
      card: action.card,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.processing,
    }),
  },

  selectSubscription: {
    cancelSubscription: (_prev, action) => ({
      canGoBack: canGoBack('cancel'),
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      step: PlanManagementStep.cancel,
    }),

    continueWithSelectSubscription: (_prev, action) => ({
      canGoBack: canGoBack('selectCard'),
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.selectCard,
    }),
  },

  success: {
    back: (_prev, action) => ({
      canGoBack: false,
      currentSubscriptionPlan: action.currentSubscriptionPlan,
      selectedSubscriptionPlan: action.selectedSubscriptionPlan,
      step: PlanManagementStep.selectSubscription,
    }),
  },
};

const initialState: PlanManagementState = {
  canGoBack: false,
  step: PlanManagementStep.selectSubscription,
};

export const withPlanManagementStateMachine =
  ({
    handlers = DEFAULT_HANDLERS,
  }: {
    handlers?: HandlersLookup;
  } = {}): Reducer<PlanManagementState, PlanManagementStateMachineAction> =>
  (prev, action) => {
    if (action.type === 'reset') {
      return {
        canGoBack: false,
        currentSubscriptionPlan: action.currentSubscriptionPlan,
        step: PlanManagementStep.selectSubscription,
      };
    }

    const stepHandlers = handlers[prev.step];
    const handler = stepHandlers?.[action.type] as Handler | undefined;
    if (!handler) return prev;

    return handler(prev, action);
  };

export const planManagementStateMachine = withPlanManagementStateMachine();

export { initialState as planManagementInitialState };
